import { Component, inject, OnInit, Signal, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";

import { Router, ActivatedRoute } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AssetService } from "../../../../services/api/asset/asset.service";
import { ButlerService } from "../../../../services/api/butler/butler.service";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { TableModule } from "@openng/optimus-ui/table";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { InstructionTableComponent } from "../instruction-table/instruction-table.component";
import { RuleChain, Instruction, Definition } from "../../../../models/api/ruleChain";
import { Column } from "../../../../models/ui/tableColumn";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import { SelectModule } from "@openng/optimus-ui/select";

@Component({
  selector: "app-rule-chain-details",
  standalone: true,
  imports: [
    TranslateModule,
    SvgIconComponent,
    TableModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    InstructionTableComponent,
  ],
  templateUrl: "./rule-chain-details.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./rule-chain-details.component.scss",
})
export class RuleChainDetailsComponent implements OnInit {
  public ruleChainDetails: WritableSignal<RuleChain | null> = signal(null);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private butlerService = inject(ButlerService);
  private assetService = inject(AssetService);
  private translate = inject(TranslateService);
  public firstInstruction!: WritableSignal<Instruction>;
  public definitions!: WritableSignal<Definition[]>;
  public gateSystems = toSignal(
    this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM).pipe(
      map((gateSystems) =>
        gateSystems.map((gateSystem) => {
          return { id: gateSystem?.id, label: gateSystem?.label };
        })
      )
    )
  );
  public definitionsColumns: Column[] = [
    { field: "label", header: "butler_programming.definitions.label" },

    { field: "type", header: "butler_programming.definitions.type" },
    {
      field: "gateSystem",
      header: "butler_programming.definitions.gate_system",
    },
    { field: "value", header: "butler_programming.definitions.value" },
  ];
  public typeOptions: Signal<DropdownOption[]> = signal([
    {
      value: "Input",
      label: this.translate.instant("butler_programming.definitions.typeOptions.Input"),
    },
    {
      value: "Flag",
      label: this.translate.instant("butler_programming.definitions.typeOptions.Flag"),
    },
    {
      value: "Relais",
      label: this.translate.instant("butler_programming.definitions.typeOptions.Relais"),
    },
    {
      value: "Timer",
      label: this.translate.instant("butler_programming.definitions.typeOptions.Timer"),
    },
  ]);
  public valueOptions: WritableSignal<Record<string, { label: string; value: number | boolean }[]>> = signal({
    Input: [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
    ],
    Relais: [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
    ],
    Flag: [
      { label: "butler_programming.valueOptions.true", value: true },
      { label: "butler_programming.valueOptions.false", value: false },
    ],
  });
  public editingRowKeys: Record<string, boolean> = {};
  public clonedDefinitions: Record<string, Definition> = {};

  // Map to save wheter the definition is being created for the first time and not just edited:
  private initialCreationDefinition = new Map<string, boolean>();
  private programId = this.activatedRoute.snapshot.params["id"];

  public ngOnInit() {
    this.setRuleChainDetails();
  }

  /**
   * Gets the rule chain details for the given program id, and sets the reactive variables ruleChainDetails, firstInstruction and definitions.
   * @param programId The id of the program.
   */
  private setRuleChainDetails() {
    this.butlerService.getRuleChainDetails(this.programId).subscribe((data) => {
      if (data) {
        this.ruleChainDetails.set(data);

        if (data.instructions) {
          data.instructions[0].nodes.map((n) => {
            if (n.definition) {
              n.definition = {
                id: n.definition?.id,
                label: n.definition.label,
                type: n.definition.type,
              };
            }
          });

          if (this.firstInstruction) {
            this.firstInstruction.set(data.instructions[0]);
          } else {
            this.firstInstruction = signal(data.instructions[0]);
          }
        }
        if (data.definitions) {
          if (this.definitions) {
            this.definitions.set(
              data.definitions.map((d) => ({
                id: d.id,
                label: d.label,
                type: d.type,
              }))
            );
          } else {
            this.definitions = signal(
              data.definitions.map((d) => ({
                id: d.id,
                label: d.label,
                type: d.type,
              }))
            );
          }
        }
      }
    });
  }

  /**
   * Navigates back to the butler page.
   */
  public goBack() {
    this.router.navigate(["/dashboard/butler"]);
  }

  /**
   * Changes the value of the definition to true if the type is flag.
   * @param def The definition to change.
   */
  public onChangeDefType(def: Definition) {
    if (def.type === "Flag") {
      def.value = true;
    }
  }

  /**
   * Adds a new definition to the rule chain and set the editing row key for the new definition.
   * This is used to add a new definition to the rule chain.
   */
  public addDefinition() {
    this.ruleChainDetails.update((prevDetails) => {
      if (prevDetails && prevDetails.definitions) {
        const defId = this.getRandomID();
        this.editingRowKeys[defId] = true;
        this.initialCreationDefinition.set(defId, true);
        prevDetails.definitions.push({
          label: "",
          id: defId,
        });
      }
      return prevDetails;
    });
  }
  /**
   * Called when the user clicks the edit button to edit a definition.
   * Makes a copy of the definition in clonedDefinitions.
   * @param def - The definition to make a copy of.
   */
  public onRowEditInit(def: Definition) {
    if (def.id) this.clonedDefinitions[def.id] = { ...def };
  }
  /**
   * Called when the user clicks the delete button to delete a definition.
   * Calls the delete definition endpoint and then refreshes the rule chain details.
   * @param def - The definition to delete.
   */
  public onRowDelete(def: Definition) {
    if (def.id) {
      this.butlerService.deleteDefinition(def.id).subscribe({
        next: () => {
          this.setRuleChainDetails();
        },
        error: () => {
          this.setRuleChainDetails();
        },
      });
    }
  }

  /**
   * Saves the edited definition to the server. If the definition is new, it will call the create definition endpoint.
   * Otherwise, it will call the edit definition endpoint.
   * After the save is done, it will refresh the rule chain details.
   * @param def - The definition to save.
   */
  public onRowEditSave(def: Definition) {
    if (def.id) {
      delete this.clonedDefinitions[def.id];
      const ruleChain = this.ruleChainDetails();
      if (this.initialCreationDefinition.get(def.id) && ruleChain?.id) {
        def.ruleChain = { id: ruleChain.id };
        delete def.id;
        this.butlerService.createDefinition(def).subscribe({
          next: () => {
            this.setRuleChainDetails();
          },
          error: () => {
            this.setRuleChainDetails();
          },
        });
      } else {
        this.butlerService.editDefinition(def).subscribe({
          next: () => {
            this.setRuleChainDetails();
          },
          error: () => {
            this.setRuleChainDetails();
          },
        });
      }
    }
  }
  /**
   * Reverts the edited definition to its original state and removes the cloned copy.
   * If the definition is new, it will be removed from the rule chain.
   * @param def - The definition to cancel.
   */
  public onRowEditCancel(def: Definition) {
    const defId = def.id;
    if (defId) {
      this.ruleChainDetails.update((ruleChain) => {
        if (ruleChain && ruleChain.definitions) {
          const index = ruleChain.definitions.findIndex((d) => d.id === def.id);
          if (index !== -1 && this.clonedDefinitions[defId] && def.id) {
            ruleChain.definitions[index] = this.clonedDefinitions[def.id];
          } else {
            ruleChain.definitions.pop();
          }
        }
        return ruleChain;
      });

      delete this.clonedDefinitions[defId];
    }
  }
  /**
   * Generates a random ID as a string that is not currently in use by existing definitions.
   * It checks against the existing IDs and ensures the generated ID is unique.
   * @returns A unique random ID as a string.
   */
  private getRandomID() {
    const excludedNumbers = this.ruleChainDetails()?.definitions?.map((def) => def.id) ?? [];

    let randomNumber: string = Math.round(Math.random() * 1000).toString();

    while (excludedNumbers.includes(randomNumber)) {
      randomNumber = Math.floor(Math.random()).toString();
    }
    return randomNumber;
  }
}
